import { useCallback } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import {
  MDSIconButton,
  MDSText,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSLensGroup,
  MDSTextFieldSlotName,
  MDSIconButtonSize,
  MDSPanelHeaderButtonGroup,
  MDSIcon,
  MDSIconKind,
  MDSFadeHorizontalScroll,
} from "@/mds";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { CollectionsListPageContactFacetFilter } from "@/pages/collections/components/collections-list-page-contact-facet-filter";
import { PageBannerSection } from "@/components/banners/page-banner-section";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { SortByDropdownButton } from "@/components/filters/sort-by-dropdown-button";
import { FilterField } from "@/components/filters/filter-field";
import { ClearFiltersModal, useClearFiltersModal } from "@/components/filters/clear-filters-modal";

export const CollectionsListPageHeader = observer(() => {
  const page = useTabPageStore("collectionsListPage");

  const onCollectionsFilterChange = useCallback(
    (value: string) => {
      page.setCollectionsFilterQuery({ query: value });
    },
    [page]
  );

  const clearCollectionsFilterQuery = useCallback(() => {
    page.setCollectionsFilterQuery({ query: "" });
  }, [page]);

  const tabs = [
    {
      label: "All",
      onClick: () =>
        page.setParams({ lens: LensKind.All, sortBy: SortByKind.LastModified, filters: {} }),
      isActive: page.lens === LensKind.All,
    },
    {
      label: "Created by me",
      onClick: () =>
        page.setParams({
          lens: LensKind.AddedByMe,
          sortBy: SortByKind.LastModified,
          filters: {},
        }),
      isActive: page.lens === LensKind.AddedByMe,
    },
    {
      label: "Shared with me",
      onClick: () =>
        page.setParams({
          lens: LensKind.SharedWithMe,
          sortBy: SortByKind.LastModified,
          filters: {},
        }),
      isActive: page.lens === LensKind.SharedWithMe,
    },
  ];

  const {
    filterButtonRef,
    showFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleToggleFilters,
    handleClearFilters,
  } = useClearFiltersModal({
    clearAllFilters: page.clearAllFilters,
    activeFiltersCount: page.activeFiltersCount,
  });

  return (
    <>
      <Wrapper>
        <MDSPanelHeader>
          <MDSText
            slot={MDSPanelHeaderSlotName.Heading}
            variant={MDSTextVariant.HeaderXl}
            numberOfLines={1}
          >
            Collections
          </MDSText>
          <MDSLensGroup slot={MDSPanelHeaderSlotName.Subsection} items={tabs} />
          <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
            <MDSTooltip
              config={{
                label: "Create collection",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                iconKind={MDSIconKind.Plus}
                onClick={page.handleCreateNewCollection}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
            <MDSTooltip
              config={{
                label: "Show filters",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                ref={filterButtonRef}
                iconKind={MDSIconKind.Filter}
                onClick={handleToggleFilters}
                isToggled={showFilters}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
          </MDSPanelHeaderButtonGroup>
        </MDSPanelHeader>
        {showFilters && (
          <WrapperBottom>
            <FiltersContainer>
              <FilterField
                placeholder="Filter by name"
                value={page.collectionsFilterQuery}
                onChange={onCollectionsFilterChange}
                onClearText={clearCollectionsFilterQuery}
                showClearTextButton={true}
                autoFocus={true}
              >
                <MDSIcon kind={MDSIconKind.Search} slot={MDSTextFieldSlotName.LeftAddon} />
              </FilterField>
              <SortByDropdownButton
                sortOptions={page.sortOptions}
                defaultSortOption={SortByKind.LastModified}
                selectedSortOption={page.sortBy}
                onSelectSortOption={page.setSortBy}
              />
              <CollectionsListPageContactFacetFilter
                buttonPrefix="Edited by"
                selectedSpaceAccountIds={page.params.filters.modifiedBy}
                onSelectItem={page.toggleContactInModifiedBy}
                onSelectClear={page.clearModifiedBy}
              />
              <CollectionsListPageContactFacetFilter
                buttonPrefix="Created by"
                selectedSpaceAccountIds={page.params.filters.createdBy}
                onSelectItem={page.toggleContactInCreatedBy}
                onSelectClear={page.clearCreatedBy}
              />
            </FiltersContainer>
          </WrapperBottom>
        )}
        <PageBannerSection />
      </Wrapper>
      <ClearFiltersModal
        triggerRef={filterButtonRef}
        isOpen={showFiltersModal}
        count={page.activeFiltersCount}
        onCancel={() => setShowFiltersModal(false)}
        onConfirm={handleClearFilters}
      />
    </>
  );
});

const Wrapper = styled.div({});

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing["4"],
}));

const FiltersContainer = styled(MDSFadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing["2"],
}));
